import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { formatMoney } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from "./Drawer";
import { Banner } from "./Banner";
import { FieldPair } from "./DataTable";
import { StatusBadge } from "./StatusBadge";
import { useToast } from "./Toast";
import type { components } from "@/api/booqly-api";
import { isDateLike, type CustomFieldValue } from "@/lib/service-detail";

/**
 * Pencil component `HKpQ9` (`v2/SidePanel — Edit`) and `qiVHk`
 * (`v2/SidePanel — Full`).
 *
 * Two modes:
 *   - `mode="edit"`: full booking shape, mirroring `NewBookingPanel`'s
 *     section layout (Client, Service, Tier + add-ons, Custom fields,
 *     Notes). Read-only sections are visibly muted because the backend
 *     `UpdateBookingRequest` only accepts customFieldValues + notes + status.
 *   - `mode="reschedule"`: tight flow — banner + the service's date field(s).
 *
 * Submit body goes through PATCH /api/v1/bookings/{id} with
 * `{ customFieldValues: [{fieldId, value}], notes? }`. The booking date lives
 * in the service's date custom field (there is no built-in start time); to
 * change client / service / tier / add-ons the operator cancels and rebooks.
 */

const schema = z.object({
  notes: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

type Selection = {
  itemId: string;
  name: string;
  price: number;
  currency: string;
  quantity: number;
};
type BookingDetail = Omit<
  components["schemas"]["BookingDetail"],
  "customFieldValues" | "selections"
> & {
  customFieldValues?: CustomFieldValue[];
  selections?: { tier?: Selection | null; addons?: Selection[] };
};

export function EditBookingPanel({
  bookingId,
  mode,
  onClose,
}: {
  bookingId: string | null;
  mode: "edit" | "reschedule";
  onClose: () => void;
}) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  const open = !!bookingId;
  const isReschedule = mode === "reschedule";

  const detail = useQuery({
    queryKey: ["booking", bookingId],
    enabled: !!accessToken && open,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/bookings/{id}", {
          params: { path: { id: bookingId! } },
        }),
      ),
  });
  const data = detail.data as BookingDetail | undefined;

  // Date custom-field editing. The booking's editable "dates" are its
  // date/datetime custom-field values (there's no built-in booking time).
  const [dateValues, setDateValues] = useState<Record<string, string>>({});
  const dateFields = (data?.customFieldValues ?? []).filter((f) =>
    isDateLike(f.type),
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  // Prefill once the booking loads — notes into the form, date custom-field
  // values into local state keyed by fieldId.
  useEffect(() => {
    if (!data) return;
    reset({ notes: data.notes ?? "" });
    const seeded: Record<string, string> = {};
    for (const f of data.customFieldValues ?? []) {
      if (isDateLike(f.type)) seeded[f.fieldId] = f.value ?? "";
    }
    setDateValues(seeded);
  }, [data, reset]);

  const update = useMutation({
    mutationFn: async (v: FormValues) => {
      // Editable "dates" are the booking's date/datetime custom-field values.
      return unwrap(
        await api.PATCH("/api/v1/bookings/{id}", {
          params: { path: { id: bookingId! } },
          body: {
            customFieldValues: dateFields.map((f) => ({
              fieldId: f.fieldId,
              value: dateValues[f.fieldId]?.trim() ?? "",
            })),
            notes: isReschedule ? undefined : (v.notes?.trim() || undefined),
          },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["booking", bookingId] });
      qc.invalidateQueries({ queryKey: ["bookings"] });
      onClose();
      toast.success(isReschedule ? "Booking rescheduled" : "Booking updated");
    },
    onError: (e) => {
      const msg = (e as Error).message;
      setError(msg);
      toast.error("Couldn't save changes", msg);
    },
  });

  const customer = data?.customer;
  const fullName =
    [customer?.firstName, customer?.lastName].filter(Boolean).join(" ") ||
    "(walk-in)";
  const initials = fullName
    .split(" ")
    .map((p) => p[0]?.toUpperCase() ?? "")
    .slice(0, 2)
    .join("");
  const tier = data?.selections?.tier ?? null;
  const addons = data?.selections?.addons ?? [];
  const tierTitle =
    (data?.service as { tierLabel?: string | null } | null | undefined)
      ?.tierLabel?.trim() || "Plan";
  // Read-only custom-fields list excludes date fields — those are edited above.
  const fieldValues = (data?.customFieldValues ?? []).filter(
    (f) => !isDateLike(f.type),
  );

  const title = isReschedule ? "Reschedule Booking" : "Edit Booking";
  const subtitle = isReschedule
    ? "Pick a new date"
    : "Update the booking details";

  return (
    <Drawer open={open} onClose={onClose} width="md">
      <DrawerHeader title={title} subtitle={subtitle} onClose={onClose} />

      <DrawerBody className="px-5 py-4">
        {detail.isLoading && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        {detail.isError && (
          <p className="text-sm text-destructive">
            {(detail.error as Error).message}
          </p>
        )}

        {data && (
          <form
            id="edit-booking-form"
            onSubmit={handleSubmit((v) => update.mutate(v))}
            className="space-y-5"
          >
            <Banner
              tone="warning"
              title={
                isReschedule
                  ? "Rescheduling moves the booking to a new date."
                  : "Only the date and notes are editable."
              }
              description={
                isReschedule
                  ? "Other booking details stay the same."
                  : "To change the client, service, tier, or add-ons, cancel this booking and create a new one."
              }
            />

            {/* Client — read-only */}
            {!isReschedule && (
              <Section title="Client" hint="(read-only)">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary inline-flex items-center justify-center text-xs font-medium shrink-0">
                    {initials || "—"}
                  </div>
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <p className="font-medium text-sm">{fullName}</p>
                    {customer?.email && (
                      <p className="text-xs text-muted-foreground truncate">
                        {customer.email}
                      </p>
                    )}
                    {customer?.phone && (
                      <p className="text-xs text-muted-foreground">
                        {customer.phone}
                      </p>
                    )}
                  </div>
                </div>
              </Section>
            )}

            {/* Service details — name read-only, date/time/duration editable */}
            <Section title="Service details">
              {!isReschedule && (
                <div className="grid grid-cols-1 gap-y-2 mb-1">
                  <FieldPair
                    label="Service"
                    value={data.service?.name ?? "—"}
                  />
                  <FieldPair
                    label="Status"
                    value={<StatusBadge label={data.status ?? "—"} />}
                  />
                </div>
              )}

              {dateFields.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  This service has no date field to edit.
                </p>
              ) : (
                <div className="grid gap-3">
                  {dateFields.map((f) => (
                    <div key={f.fieldId} className="grid gap-1.5">
                      <Label htmlFor={`date-${f.fieldId}`}>{f.label}</Label>
                      <Input
                        id={`date-${f.fieldId}`}
                        type={f.type === "datetime" ? "datetime-local" : "date"}
                        value={dateValues[f.fieldId] ?? ""}
                        onChange={(e) =>
                          setDateValues((prev) => ({
                            ...prev,
                            [f.fieldId]: e.target.value,
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Tier + add-ons — read-only summary, only when present */}
            {!isReschedule && (tier || addons.length > 0) && (
              <Section
                title={`${tierTitle}${addons.length > 0 ? " & add-ons" : ""}`}
                hint="(read-only)"
              >
                <ul className="rounded-md border bg-card divide-y">
                  {tier && (
                    <li className="flex items-center justify-between gap-3 px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                          {tierTitle}
                        </p>
                        <p className="text-sm font-medium truncate">
                          {tier.name}
                        </p>
                      </div>
                      <span className="text-sm font-semibold tabular-nums">
                        {formatMoney(tier.currency, tier.price)}
                      </span>
                    </li>
                  )}
                  {addons.map((a) => (
                    <li
                      key={a.itemId}
                      className="flex items-center justify-between gap-3 px-3 py-2.5"
                    >
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                          Add-on
                        </p>
                        <p className="text-sm truncate">
                          {a.name}
                          {a.quantity > 1 && (
                            <span className="text-muted-foreground">
                              {" "}× {a.quantity}
                            </span>
                          )}
                        </p>
                      </div>
                      <span className="text-sm tabular-nums">
                        +{formatMoney(a.currency, a.price)}
                      </span>
                    </li>
                  ))}
                  <li className="flex items-center justify-between gap-3 px-3 py-2.5 bg-muted/30">
                    <p className="text-sm font-semibold">Total</p>
                    <p className="text-sm font-bold tabular-nums">
                      {formatMoney(data.currency, data.price)}
                    </p>
                  </li>
                </ul>
              </Section>
            )}

            {/* Custom fields — read-only, only when the booking has any */}
            {!isReschedule && fieldValues.length > 0 && (
              <Section title="Custom fields" hint="(read-only)">
                <div className="grid gap-x-6 gap-y-1">
                  {fieldValues.map((f) => (
                    <FieldPair
                      key={f.fieldId}
                      label={f.label}
                      value={
                        f.value && f.value.trim() !== "" ? (
                          f.type === "checkbox" ? (
                            f.value === "true" ? "Yes" : "No"
                          ) : (
                            f.value
                          )
                        ) : (
                          <span className="text-muted-foreground italic">
                            Not provided
                          </span>
                        )
                      }
                    />
                  ))}
                </div>
              </Section>
            )}

            {/* Notes — editable in edit mode only */}
            {!isReschedule && (
              <Section title="Notes">
                <textarea
                  rows={4}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="Internal notes about this booking…"
                  {...register("notes")}
                />
              </Section>
            )}

            {error && (
              <p className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
                {error}
              </p>
            )}
          </form>
        )}
      </DrawerBody>

      <DrawerFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="edit-booking-form"
          size="sm"
          disabled={isSubmitting || update.isPending || !data}
        >
          {update.isPending
            ? "Saving…"
            : isReschedule
              ? "Reschedule"
              : "Save changes"}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
        {title}
        {hint && (
          <span className="ml-1.5 text-muted-foreground/70 normal-case tracking-normal font-normal">
            {hint}
          </span>
        )}
      </p>
      {children}
    </section>
  );
}
